import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import EventCard from "@/components/base/EventCard";
import useSEO from "@/hooks/useSEO";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/site";

const origin = typeof window !== "undefined" ? window.location.origin : "";
const yearOptions = Array.from(new Set(events.map((item) => item.year))).sort((a, b) =>
  a < b ? 1 : -1
);

export default function Archive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "all";
  const year = searchParams.get("year") || "all";
  const isReview = searchParams.get("view") === "review";

  const [input, setInput] = useState(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const toggleReview = () => {
    const next = new URLSearchParams(searchParams);
    if (isReview) {
      next.delete("view");
    } else {
      next.set("view", "review");
    }
    setSearchParams(next, { replace: true });
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setParam("q", input.trim());
  };

  const filtered = useMemo(() => {
    const kw = q.trim();
    return events
      .filter((item) => {
        const matchKeyword =
          !kw ||
          item.title.includes(kw) ||
          item.summary.includes(kw) ||
          item.categoryName.includes(kw) ||
          item.tags.some((tag) => tag.includes(kw));
        const matchCategory = category === "all" || item.category === category;
        const matchYear = year === "all" || item.year === year;
        const matchReview = !isReview || Number(item.year) <= 2023;
        return matchKeyword && matchCategory && matchYear && matchReview;
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [q, category, year, isReview]);

  const hasFilter = Boolean(q) || category !== "all" || year !== "all" || isReview;

  useSEO({
    title: isReview
      ? "历史复盘 · 旧瓜档案库 | 爆料专区"
      : "年度盘点 · 娱乐大事件档案库 | 爆料专区",
    description:
      "浏览爆料专区的全部事件档案，按情感、综艺、影视、乐坛、代言、偶像六大主题以及年份筛选，支持关键词搜索。每一篇都附带完整时间线与后续进展。",
    keywords: "爆料专区,年度盘点,事件档案库,旧瓜复盘,历史热点,新闻回顾,按年份浏览",
    canonical: "/archive",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "娱乐大事件档案库",
        description:
          "按主题分类与年份整理的事件档案库，收录情感、综艺、影视、乐坛、代言与偶像六大线索。",
        url: `${origin}/archive`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: "爆料专区", url: `${origin}/` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "年度盘点", item: `${origin}/archive` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "事件档案列表",
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 10).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `${origin}/event/${item.id}`,
        })),
      },
    ],
  });

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: isReview ? "历史复盘" : "年度盘点" },
            ]}
          />
          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.24em] text-primary-600">
                <span className="inline-block h-3 w-1 rounded-full bg-primary-500" />
                ARCHIVE
              </p>
              <h1 className="font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {isReview ? "历史复盘 · 旧瓜档案库" : "年度盘点 · 娱乐大事件档案库"}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {isReview
                  ? "这里集中收录 2023 年及更早的事件档案。同一条线索横跨多个年份，按主题筛选后顺着时间往下读，更容易看清一件事的来龙去脉。"
                  : "全部事件档案按主题与年份归档，共六条主要线索。支持关键词搜索，也可以用下面的筛选条快速缩小范围。"}
              </p>
            </div>
            <div className="shrink-0 rounded-lg border border-background-200 bg-background-50 px-5 py-4">
              <p className="text-xs text-foreground-500">当前筛选结果</p>
              <p className="mt-1 font-heading text-3xl font-black text-primary-600">
                {filtered.length}
                <span className="ml-1 text-sm font-normal text-foreground-500">
                  / {events.length} 篇档案
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur-md md:top-20">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-4 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <form onSubmit={handleSearch} className="flex w-full items-center gap-2 lg:max-w-md" role="search">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  type="search"
                  name="archive-keyword"
                  placeholder="输入事件名、关键词或标签"
                  aria-label="在档案库中搜索"
                  className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-2-line" />
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={toggleReview}
              className={`inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap self-start rounded-full border px-4 text-sm font-medium transition-colors lg:self-auto ${
                isReview
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              <i className="ri-history-line" />
              只看旧瓜复盘（2023 及以前）
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">主题</span>
            <button
              type="button"
              onClick={() => setParam("category", "all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                category === "all"
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setParam("category", cat.id)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  category === cat.id
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="mr-1 text-xs text-foreground-500">年份</span>
            <button
              type="button"
              onClick={() => setParam("year", "all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                year === "all"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              全部年份
            </button>
            {yearOptions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setParam("year", item)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  year === item
                    ? "bg-secondary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {item} 年
              </button>
            ))}
            {hasFilter && (
              <button
                type="button"
                onClick={resetAll}
                className="ml-1 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                <i className="ri-close-circle-line" />
                清空筛选
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                没有找到匹配的档案
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                换一个关键词，或者放宽主题与年份的筛选条件试试。也可以直接把线索提交给我们。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" />
                重置全部筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}